import React from 'react';
import {
  Bar, BarChart, CartesianGrid, Cell, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { Radar, Link2, CircleAlert, EyeOff } from 'lucide-react';

// Palette. Validated with the dataviz palette validator against a white chart surface:
//   categorical/status trio #16a34a / #7c3aed / #b45309 — all six checks pass
//   sequential ramp below — strictly decreasing lightness (0.81 → 0.11)
// The lightest ramp step sits at 1.2:1 against white, so bars carry direct labels and a
// hairline border rather than relying on fill alone.
const STATUS = {
  good: '#16a34a',    // --cmp-done
  neutral: '#7c3aed', // --cmp-line
  warn: '#b45309',
};

// Ordered buckets are magnitude, not identity: one hue, light → dark, strongest darkest.
const SIMILARITY_RAMP = ['#b91c1c', '#cf4545', '#e07b7b', '#f19393', '#fbb8b8', '#fde2e2'];

const AXIS = { stroke: '#6b7280', fontSize: 11 };
const GRID = '#e2d9f3';

function pct(n, d) {
  if (!d) return 0;
  return Math.round((n / d) * 1000) / 10;
}

function ChartCard({ title, hint, children, wide }) {
  return (
    <div className={`dup-stat-card${wide ? ' wide' : ''}`}>
      <div className="dup-stat-card-title">
        {title}
        {hint ? <span className="dup-stat-hint" title={hint}><CircleAlert size={12} /></span> : null}
      </div>
      {children}
    </div>
  );
}

function Tile({ icon, label, value, sub, tone }) {
  return (
    <div className={`dup-tile${tone ? ` ${tone}` : ''}`}>
      <div className="dup-tile-label">{icon}{label}</div>
      <div className="dup-tile-value">{value}</div>
      {sub ? <div className="dup-tile-sub">{sub}</div> : null}
    </div>
  );
}

/** Horizontal magnitude bars — one measure, one hue, direct-labelled. */
function DebtBars({ data, color }) {
  return (
    <ResponsiveContainer width="100%" height={Math.max(150, data.length * 26 + 20)}>
      <BarChart data={data} layout="vertical" margin={{ top: 4, right: 44, bottom: 4, left: 4 }}>
        <CartesianGrid horizontal={false} stroke={GRID} />
        <XAxis type="number" {...AXIS} allowDecimals={false} />
        <YAxis type="category" dataKey="label" width={165} {...AXIS} interval={0} />
        <Tooltip
          formatter={(v) => [`${v} unlinked`, 'Duplicates']}
          contentStyle={{ fontSize: 12, borderRadius: 6, border: `1px solid ${GRID}` }}
        />
        <Bar dataKey="count" fill={color} barSize={14} radius={[0, 4, 4, 0]}>
          <LabelList dataKey="count" position="right" style={{ fill: '#6b7280', fontSize: 11 }} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

export default function StatsTab({ stats, sessionLinkedKeys }) {
  if (!stats) return <div className="section-loading">Loading…</div>;

  const { totals, linking, funnel, debtByProject, debtByUnit, similarityBuckets, matcher } = stats;

  // Links made in this session count toward the headline, so the carrot visibly moves.
  const sessionLinked = sessionLinkedKeys?.length ?? 0;
  const linkedNow = linking.linked + sessionLinked;
  const linkedPct = pct(linkedNow, linking.resolvedDuplicate);

  const funnelData = funnel.map(f => ({
    ...f,
    fill: f.state === 'Resolved as Duplicate' ? STATUS.good
      : f.state === 'Still open' ? STATUS.warn
        : STATUS.neutral,
  }));

  const recallPct = pct(linking.knownDuplicatesFlagged, linking.resolvedDuplicate);
  const coveragePct = pct(totals.withEmbedding, totals.bugs);

  return (
    <div className="dup-stats">

      {/* ── The headline: how much of the duplicate backlog is actually linked ── */}
      <div className="dup-stat-card wide">
        <div className="dup-stat-card-title">Duplicates linked in Jira</div>
        <div className="dup-progress-head">
          <span className="dup-progress-value">{linkedNow}</span>
          <span className="dup-progress-of">of {linking.resolvedDuplicate} bugs are correctly linked to their duplicate</span>
          <span className={`dup-progress-pct${linkedPct > 0 ? ' moving' : ''}`}>{linkedPct}%</span>
        </div>
        <div className="dup-progress-track">
          <div
            className="dup-progress-fill"
            style={{ width: `${Math.max(linkedPct, linkedNow > 0 ? 1.5 : 0)}%` }}
          />
        </div>
        <div className="dup-progress-foot">
          Every bug closed as a duplicate should carry a <code>duplicates</code> link to the
          ticket it duplicates. This bar is the one number to move.
          {sessionLinked > 0 && (
            <strong> {sessionLinked} linked in this session (prototype, not saved).</strong>
          )}
        </div>
      </div>

      <div className="dup-tile-row">
        <Tile
          icon={<Radar size={13} />}
          label="Known duplicates the matcher caught"
          value={`${recallPct}%`}
          sub={`${linking.knownDuplicatesFlagged} of ${linking.resolvedDuplicate}`}
        />
        <Tile
          icon={<Link2 size={13} />}
          label="Linked but not marked as duplicate"
          value={linking.linkedNotResolvedDuplicate}
          sub="has a duplicates link, not resolved as Duplicate"
        />
        <Tile
          icon={<EyeOff size={13} />}
          label="Bugs with no embedding"
          value={totals.withoutEmbedding}
          sub={`${coveragePct}% of ${totals.bugs} covered`}
        />
      </div>

      {/* ── Is anyone acting on what the matcher finds? ── */}
      <div className="dup-stat-grid">
        <ChartCard
          title={`What happened to the ${totals.flagged} flagged bugs`}
          hint="Bugs with at least one match at or above the 0.6 threshold, by how they were eventually resolved."
        >
          <ResponsiveContainer width="100%" height={170}>
            <BarChart data={funnelData} layout="vertical" margin={{ top: 4, right: 52, bottom: 4, left: 4 }}>
              <CartesianGrid horizontal={false} stroke={GRID} />
              <XAxis type="number" {...AXIS} allowDecimals={false} />
              <YAxis type="category" dataKey="state" width={165} {...AXIS} interval={0} />
              <Tooltip
                formatter={(v) => [`${v} bugs (${pct(v, totals.flagged)}%)`, 'Count']}
                contentStyle={{ fontSize: 12, borderRadius: 6, border: `1px solid ${GRID}` }}
              />
              <Bar dataKey="count" barSize={16} radius={[0, 4, 4, 0]}>
                {funnelData.map(d => <Cell key={d.state} fill={d.fill} />)}
                <LabelList
                  dataKey="count"
                  position="right"
                  formatter={(v) => `${v}  (${pct(v, totals.flagged)}%)`}
                  style={{ fill: '#6b7280', fontSize: 11 }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Match strength"
          hint="Unique ticket pairs by similarity score. Darker is a stronger match."
        >
          <ResponsiveContainer width="100%" height={170}>
            <BarChart data={similarityBuckets} margin={{ top: 18, right: 8, bottom: 4, left: 4 }}>
              <CartesianGrid vertical={false} stroke={GRID} />
              <XAxis dataKey="bucket" {...AXIS} interval={0} />
              <YAxis {...AXIS} allowDecimals={false} />
              <Tooltip
                formatter={(v) => [`${v} pairs`, 'Count']}
                contentStyle={{ fontSize: 12, borderRadius: 6, border: `1px solid ${GRID}` }}
              />
              <Bar dataKey="pairs" barSize={26} radius={[4, 4, 0, 0]} stroke="#d9c9c9">
                {similarityBuckets.map((d, i) => (
                  <Cell key={d.bucket} fill={SIMILARITY_RAMP[i] ?? SIMILARITY_RAMP[SIMILARITY_RAMP.length - 1]} />
                ))}
                <LabelList dataKey="pairs" position="top" style={{ fill: '#6b7280', fontSize: 11 }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* ── Calibration. Separate chart, never a second axis on the one above. ── */}
      <ChartCard
        title="Does a higher score mean a real duplicate?"
        hint="Share of pairs in each bucket where either ticket was resolved as Duplicate."
        wide
      >
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={similarityBuckets} margin={{ top: 18, right: 8, bottom: 4, left: 4 }}>
            <CartesianGrid vertical={false} stroke={GRID} />
            <XAxis dataKey="bucket" {...AXIS} interval={0} />
            <YAxis {...AXIS} unit="%" domain={[0, 50]} />
            <Tooltip
              formatter={(v, n, p) => [`${v}% (${p.payload.confirmed} of ${p.payload.pairs} pairs)`, 'Either side marked Duplicate']}
              contentStyle={{ fontSize: 12, borderRadius: 6, border: `1px solid ${GRID}` }}
            />
            <Bar dataKey="confirmedPct" fill={STATUS.neutral} barSize={26} radius={[4, 4, 0, 0]}>
              <LabelList dataKey="confirmedPct" position="top" formatter={(v) => `${v}%`} style={{ fill: '#6b7280', fontSize: 11 }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        <div className="dup-stat-caveat">
          <CircleAlert size={13} />
          <span>
            <strong>Read this with care.</strong> With almost no duplicate links in Jira, the only
            available ground truth is “one of these two tickets was closed as a duplicate of
            something” — not that these two are a pair. The rate barely rises with score, but that
            is as likely to be a bad yardstick as a bad matcher. Linking is what turns this into a
            real precision measure.
          </span>
        </div>
      </ChartCard>

      {/* ── Where the work is ── */}
      <div className="dup-stat-grid">
        <ChartCard title="Unlinked duplicates by project" hint="Where the linking backlog sits.">
          <DebtBars data={debtByProject} color="#b91c1c" />
        </ChartCard>
        <ChartCard title="Unlinked duplicates by unit" hint="Same backlog, by product unit.">
          <DebtBars data={debtByUnit} color="#b91c1c" />
        </ChartCard>
      </div>

      {/* ── Matcher internals: useful when debugging the matcher, not when triaging ── */}
      <details className="dup-stat-details">
        <summary>Matcher internals</summary>
        <div className="dup-internals">
          <div><span>Stored match rows</span><strong>{matcher.storedRows}</strong></div>
          <div><span>Distinct source tickets</span><strong>{matcher.distinctSourceTickets}</strong></div>
          <div><span>Max candidates per source row</span><strong>{matcher.maxCandidatesPerSourceRow}</strong></div>
          <div>
            <span>Max candidates read both ways</span>
            <strong>{matcher.maxCandidatesSymmetric}</strong>
          </div>
          <div><span>Avg candidates read both ways</span><strong>{matcher.avgCandidatesSymmetric}</strong></div>
        </div>
        <p className="dup-stat-caveat plain">
          The per-source cap is {matcher.maxCandidatesPerSourceRow}, but a ticket also appears in
          other tickets’ candidate lists — read symmetrically a bug can have up to{' '}
          {matcher.maxCandidatesSymmetric}, which is what this tool shows.
        </p>
      </details>

      <p className="dup-stat-footer">
        {stats.scope} Threshold ≥ {stats.threshold}. Snapshot taken {stats.generatedAt}.
      </p>
    </div>
  );
}
